// Borrowing a survey definition from a sibling response (card #4117).
//
// When a mission is not saved on the phone and the server cannot be reached,
// the Tasker can sometimes still be let in: another response of the SAME
// mission may already be cached, and a response's survey_json is a copy of its
// mission's survey (Response#copy_survey_json_from_mission). Borrowing is only
// safe when that copy is provably identical, so every guard below must pass or
// nothing is borrowed and the editor shows "This mission isn't downloaded yet".
//
// What is borrowed: ONLY the survey definition (the questions).
// What is never borrowed: answers, place data, the response id, the complete
// url. The editor takes those from the page the server rendered for the actual
// response, so answers are saved and submitted against the actual response id
// and place variables come from that response's own place.
//
// The facts about the actual response ("page") come from data attributes the
// server renders into the editor page (surveys/results/_edit.html.erb):
//   missionId       the response's own mission
//   surveyDigest    fingerprint of the response's own survey_json
//   serverResultBlank  the server holds no answers for it yet
//   status          todo | scheduled | started | ended
// (The response's own ranging hints also come from the page, not the sibling.)
//
// Cached rows carry missionId and surveyDigest as stamped from the server at
// sync time (upsertCachedResponse). A row cached before this card has neither,
// so it is never a candidate.

const BORROWABLE_STATUSES = ["todo", "scheduled", "started"]

function hasQuestions(surveyJson) {
  return !!surveyJson && typeof surveyJson === "object" && Array.isArray(surveyJson.pages)
}

// Pure decision, so every guard is testable on its own.
// Returns { ok: true } or { ok: false, reason }.
export function checkPageGuards(page) {
  if (!page.missionId) return { ok: false, reason: "no_mission_id" }
  if (!page.surveyDigest) return { ok: false, reason: "no_survey_digest" }
  if (!BORROWABLE_STATUSES.includes(page.status)) return { ok: false, reason: "status_not_open" }
  // Saving replaces the response's whole result_json, so a response that
  // already holds answers on the server must be fetched, not borrowed.
  if (!page.serverResultBlank) return { ok: false, reason: "server_has_answers" }
  return { ok: true }
}

// Looks through the phone's cached responses for a sibling that can lend its
// survey. Returns { sibling, surveyJson, mismatched } or { reason, mismatched }.
//
// A candidate must: belong to the same mission, carry the identical survey
// digest, be another response (never the one being opened), and hold a real
// survey. A same-mission row with a different digest is counted, never used.
export async function findBorrowableSurvey(db, page) {
  const guard = checkPageGuards(page)
  if (!guard.ok) return { reason: guard.reason, mismatched: 0 }

  const ownId = Number(page.responseId)
  const missionId = Number(page.missionId)
  const rows = await db.cached_responses.toArray()

  let mismatched = 0
  const candidates = []
  for (const row of rows) {
    if (Number(row.responseId) === ownId) continue
    if (row.missionId == null || Number(row.missionId) !== missionId) continue
    if (!row.surveyDigest || !hasQuestions(row.surveyJson)) continue
    if (row.surveyDigest !== page.surveyDigest) { mismatched++; continue }
    candidates.push(row)
  }

  if (candidates.length === 0) return { reason: "no_matching_sibling", mismatched }

  // Deterministic: the most recently cached copy.
  candidates.sort((a, b) => String(b.cachedAt || "").localeCompare(String(a.cachedAt || "")))
  const sibling = candidates[0]
  return { sibling, surveyJson: structuredClone(sibling.surveyJson), mismatched }
}
